import {GridModel} from '@xh/hoist/cmp/grid';
import {hbox, hspacer} from '@xh/hoist/cmp/layout';
import {HoistModel, managed, TaskObserver, XH} from '@xh/hoist/core';
import {fmtDateTimeSec} from '@xh/hoist/format';
import {Icon} from '@xh/hoist/icon';
import {action, observable} from '@xh/hoist/mobx';
import {LogEvent} from '../../data/DataTypes';
import {isEmpty, last} from 'lodash';
import moment from 'moment';
import {PanelModel} from '@xh/hoist/desktop/cmp/panel';

export class WorkflowLogModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @observable accessor isVisible: boolean;
    @managed panelModel = this.createPanelModel();
    private executionId: number;
    private lastEventId: number;
    private refreshTask;

    @managed executionLogGrid = this.createExecutionLogGridModel();

    @action open(id: number) {
        clearTimeout(this.refreshTask);

        this.panelModel.setIsModal(false);
        this.isVisible = true;
        this.executionId = id;
        this.lastEventId = -1;
        this.executionLogGrid.clear();
        this.showLog().linkTo(this.apiTask);
    }

    @action close() {
        this.isVisible = false;
        clearTimeout(this.refreshTask);
    }

    @action refresh() {
        clearTimeout(this.refreshTask);
        this.lastEventId = -1;
        this.executionLogGrid.clear();
        this.showLog().linkTo(this.apiTask);
    }

    private async showLog() {
        const events = await XH.workflowService.fetchWorkflowLogAsync(this.executionId, this.lastEventId);

        const data = this.mapRawData(events);
        this.executionLogGrid.updateData(data);

        if (!isEmpty(data)) {
            this.lastEventId = last(events)?.id;
            this.executionLogGrid.ensureRecordsVisibleAsync(last(this.executionLogGrid.store.allRecords));
        }

        const execution = await XH.workflowService.executions.get(this.executionId);
        if (execution) {
            if (execution.completed && moment(execution.endTime) <= moment().subtract(10, 'seconds')) {
                return;
            }
        }

        this.refreshTask = setTimeout(() => this.showLog(), 1000);
    }

    private mapRawData(events: LogEvent[]): LogEvent[] {
        const data: LogEvent[] = [];
        events.forEach(e => {
            data.push({...e, type: 'message'});
            if (e.exception) {
                const lines = e.exception.split('\n');
                data.push(
                    ...lines
                        .filter(l => l)
                        .map((l, idx) => {
                            return {
                                type: 'exception' as const,
                                id: idx,
                                timestamp: null,
                                level: 'Error',
                                message: l
                            };
                        })
                );
            }
        });
        return data;
    }

    private createExecutionLogGridModel(): GridModel {
        return new GridModel({
            persistWith: {prefKey: 'workflowsTabState', path: 'logGrid'},
            store: {idSpec: () => XH.genId()},
            stripeRows: false,
            sizingMode: 'tiny',
            rowClassFn: record => `workflow-log__event  ${record.data.level}`,
            colDefaults: {sortable: false},
            columns: [
                {
                    field: 'idx',
                    minWidth: 60,
                    renderer: (v, {record}) => (record.raw.type == 'message' ? record.raw.id : null)
                },
                {
                    field: 'level',
                    minWidth: 30,
                    headerName: null,
                    renderer: (v, {record}) => {
                        if (record.raw.type == 'exception') return null;
                        if (v == 'Information') return Icon.info();
                        if (v == 'Warning') return Icon.warning();
                        if (v == 'Error') return Icon.error();
                    }
                },
                {
                    field: 'timestamp',
                    minWidth: 180,
                    renderer: v => fmtDateTimeSec(v, {fmt: 'YYYY-MM-DD HH:mm:ss z'})
                },
                {field: 'sourceContext', hidden: true},
                {
                    field: 'message',
                    minWidth: 1000,

                    renderer: (v, {record}) => (record.raw.type == 'message' ? v : hbox(hspacer(25), v))
                }
            ]
        });
    }

    private createPanelModel(): PanelModel {
        return new PanelModel({
            defaultSize: 300,
            collapsible: false,
            resizable: true,
            side: 'right',
            modalSupport: true,
            persistWith: {prefKey: 'worklowLogPanelSize'}
        });
    }
}
